import React from 'react';
import {Col, Input, Row} from 'antd';
import {filter, isEmpty, join, map, not, pipe, reject, replace, split, trim} from 'ramda';
import {memo, useCallback, useEffect, useState} from 'react';


interface ChangeEvent {
  target: {
    value: React.SetStateAction<string>;
  }
};

const joinNonEmpty = ( glue: string ) => pipe(
  filter((x: string) => !!x && !isEmpty(x)), //not falsy NOR empty
  // reject((v: string) => isEmpty(v)),
  map(trim),
  join(glue),
);

const PostalAddressField = (props: any): JSX.Element => {
  const { label, labelSpan=5, fieldSpan, initialValue, onChange } = props;
  const [value, setValue] = useState('');

  useEffect(() => setValue(initialValue));

  return (
    <>
      <Col
        span={labelSpan}
        style={{ textAlign: 'right', padding: '4px 8px 8px 0', }}
        >
        {label}
      </Col>
      <Col span={fieldSpan}>
        <Input value={value} onChange={onChange} />
      </Col>
    </>
  );
};

const PostalAddress = (props: any): JSX.Element => {
  const [streetAddress, setStreetAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zip, setZip] = useState('');
  const [country, setCountry] = useState('US');

  const [editable, setEditable] = useState(false);
  
  useEffect(
    () => {
      const address = props?.address ?? '';
      if (not(isEmpty(address))) {
        const [streetAddress, city, state, zip, country] = split(', ', address ?? '');
        setStreetAddress(streetAddress);
        setCity(city);
        setState(state);
        setZip(zip);
        setCountry(country);
      }
    },
    []
  );

  useEffect(
    () => {
      if (typeof props?.onChange === 'function') {
        props.onChange(joinNonEmpty(', ')([streetAddress, city, state, zip, country]));
      }
    },
    [streetAddress, city, state, zip, country]
  );

  const onChangeStreetAddress = useCallback(
    (e: ChangeEvent) => setStreetAddress(replace(/,/g, '', e?.target?.value.toString())),
    [editable]
  );
  const onChangeCity = useCallback(
    (e: ChangeEvent) => setCity(e?.target?.value),
    [editable]
  );
  const onChangeState = useCallback(
    (e: ChangeEvent) => setState(e?.target?.value),
    [editable]
  );
  const onChangeZip = useCallback(
    (e: ChangeEvent) => {
      const zip: string = e?.target?.value.toString();
      const reg = /^\d*$/;
      if (reg.test(zip) && !isNaN(Number(zip))) {
        setZip(e?.target?.value);
      }
    },
    [editable]
  );
  const onChangeCountry = useCallback(
    (e: ChangeEvent) => setCountry(e?.target?.value),
    [editable]
  );

  return (<>
    <Row style={{ padding: '0 0 32px 0' }}>
      <Col span={24}>
        <Input.TextArea
          showCount
          allowClear
          autoSize={{ minRows: 2, maxRows: 4 }}
          readOnly
          value={joinNonEmpty(', ')([streetAddress, city, state, zip, country])}
        />
      </Col>
    </Row>

    <Row style={{ padding: '8px 0' }}>
      <PostalAddressField
        label='Street address'
        fieldSpan={19} 
        initialValue={streetAddress}
        onChange={onChangeStreetAddress}
      />
    </Row>

    <Row style={{ padding: '8px 0' }}>
      <PostalAddressField
        label='City'
        fieldSpan={7}
        initialValue={city}
        onChange={onChangeCity}
      />
      <PostalAddressField
        label='State'
        fieldSpan={7}
        initialValue={state}
        onChange={onChangeState}
      />
    </Row>

    <Row style={{ padding: '8px 0' }}>
      <PostalAddressField
        label='Zip code'
        fieldSpan={7}
        initialValue={zip}
        onChange={onChangeZip}
      />

      <PostalAddressField
        label='Country'
        fieldSpan={7}
        initialValue={country}
        onChange={onChangeCountry}
      />
    </Row>
  </>);
}

export default memo(PostalAddress);
